import '@main';
import type { UserSegmentMap } from "@js/types/entity";
import type { Question } from "@js/types/entity";
import type { Option } from "@js/types/entity";
import type { UserAnswer } from '@js/types/entity';
import { Modal } from 'bootstrap';

await u.import('@vendor/ts-luxon/dist/ts-luxon.umd.js');

const DateTime = tsLuxon.DateTime;
const lessonId: number = u.data('lessonId');
const totalAssignment: number = u.data('totalAssignment');
const lessonSectionOrder: number[] = u.data('lessonSectionOrder');
const ownedLesson: boolean = u.data('ownedLesson');
const currentSegmentId: number = u.data('currentSegmentId');

// prepare init assignments
const res = await u.$http.get(
  '@ajax_lesson/prepareStudentAssignments',
  {
    params: {
      lesson_id: lessonId,
      limit: 5,
      offset: 0,
    }
  }
);

let assignments: UserSegmentMap[] = res.data.data;

let nextOffset: number = assignments.length;

await u.domready()
  .then(() => {
    // favorite btn
    const favBtns = document.querySelectorAll('.j-favorite-btn');

    if (favBtns.length) {
      favBtns.forEach((favBtn) => {
        favBtn.addEventListener('click', () => {
          favBtn.classList.toggle('active');
        });
      });
    }

    // qa
    const qaFaviconBtns = document.querySelectorAll('.j-qa-favicon');

    if (qaFaviconBtns.length) {
      qaFaviconBtns.forEach((qaFaviconBtn) => {
        qaFaviconBtn.addEventListener('click', () => {
          qaFaviconBtn.classList.toggle('active');
        });
      });
    }

    // homework list
    const homeworkList = document.querySelector<HTMLDivElement>('.j-hw-list');

    appendStudentAssignmentList(assignments);

    // get more assignments
    const getMoreAssignments = document.querySelector<HTMLAnchorElement>('.j-get-student-assignments');

    toggleMoreButton();

    getMoreAssignments?.addEventListener('click', async () => {
      const res = await u.$http.get(
        '@ajax_lesson/prepareStudentAssignments',
        {
          params: {
            lesson_id: lessonId,
            limit: 5,
            offset: nextOffset,
          }
        }
      );

      assignments = res.data.data;

      nextOffset = nextOffset + assignments.length;

      appendStudentAssignmentList(res.data.data);

      toggleMoreButton();
    });

    function appendStudentAssignmentList(assignments: UserSegmentMap[]) {
      if (totalAssignment <= 0) {
        let homeworkItem = document.createElement('li');

        homeworkItem.innerHTML += `
        <div class="h6 mb-0 py-40 text-center text-gray-500">
            無作業內容
        </div>
      `;

        homeworkList?.appendChild(homeworkItem);

        return;
      }

      assignments.forEach((assignment: UserSegmentMap) => {
        let homeworkItem = document.createElement('li');

        const sectionName: string = getSectionName(lessonSectionOrder, assignment.segmentId);

        const date = DateTime.fromISO(assignment.assignmentUploadTime, { zone: 'UTC' })
          .setZone('Asia/Taipei')
          .toFormat('yyyy-MM-dd');

        // Append the avatar section
        homeworkItem.innerHTML += `
        <a href="${assignment.assignment}" download> 
          <div class="d-flex justify-content-between align-items-center mb-12 text-gray-600">
            <div class="d-flex gap-20 align-items-center">
                <div class="c-qa-avatar rounded-pill overflow-hidden">
                    <img src="${assignment.user.avatar}"
                        alt="${assignment.user.nickname !== '' ? assignment.user.nickname : '匿名'}"
                        class="w-100 h-100 object-fit-cover">
                </div>
                ${assignment.user.nickname !== '' ? assignment.user.nickname : '匿名'}
                <span class="d-none d-md-inline">|</span><br class="d-md-none" />
                ${date}
            </div>
          </div>
          <div class="text-primary mb-1">
              ${sectionName}
          </div>
          <div class="d-md-flex gap-20">
              <div class="c-hw-list__img-wrapper overflow-hidden mb-20 mb-md-0">
                  <img src="./assets/images/front/demo/card-demo.jpg"
                      alt="${sectionName}"
                      class="w-100 h-100 object-fit-cover">
              </div>
              <div class="text-gray-600">
                ${assignment?.description.replace(/\r\n/g, '<br>')}
              </div>
          </div>
        </a>
      `;

        homeworkList?.appendChild(homeworkItem);
      });
    }

    function toggleMoreButton() {
      if (totalAssignment <= 0) {
        getMoreAssignments?.classList.add('d-none');
        return;
      }

      if (nextOffset + 1 >= totalAssignment) {
        getMoreAssignments?.classList.add('d-none');
      } else {
        getMoreAssignments?.classList.remove('d-none');
      }
    }

    // modal
    const homeworkModal = new bootstrap.Modal('#homework-modal', {});
    const quizModal = new bootstrap.Modal('#quiz-modal', {});
    const quizResultModal = new bootstrap.Modal('#quiz-result-modal', {});

    const modalBtn = document.querySelectorAll<HTMLButtonElement>('.j-section-modal');

    modalBtn.forEach((button: HTMLButtonElement) => {
      button.addEventListener('click', async (e: Event) => {
        const target = e.currentTarget as HTMLButtonElement;

        const type = target.dataset.type ?? null;
        const sectionId = target.dataset!.sectionId ?? '0';
        const title = target.dataset!.sectionTitle ?? '';
        const index = target.dataset!.sectionIndex ?? null;
        const content = target.dataset!.sectionContent ?? '';
        const status = target.dataset!.sectionStatus ?? 'progress';
        const link = target.dataset!.sectionLink ?? 'javascript://';
        const sectionMapId = target.dataset!.sectionMapId ?? undefined;

        let isPreview = false;

        await u.$http.get(
          '@ajax_lesson/checkSectionPreview',
          {
            params: {
              segment_id: sectionId,
            }
          }
        )
          .then(async (res) => {
            isPreview = res.data.data;
          });

        if (!ownedLesson) {
          if (!isPreview) {
            await swal('購買課程後即可查看內容');
            return;
          }
        }

        const prevRes = await u.$http.get(
          '@ajax_lesson/prevSegmentDone',
          {
            params: {
              segment_id: sectionId,
            }
          }
        );

        if (!prevRes.data.data) {
          await swal('請先完成前一個章節');
          return;
        }

        if (type === 'video') {
          window.location.href = link;
        }

        if (type === 'homework') {
          document.querySelector<HTMLDivElement>('.j-homework-title')!.textContent = title;
          document.querySelector<HTMLDivElement>('.j-homework-index')!.textContent = index;
          document.querySelector<HTMLDivElement>('.j-homework-content')!.innerHTML = content;
          document.querySelector<HTMLInputElement>('.j-homework-section-id')!.value = sectionId;


          const res = await u.$http.get(
            '@ajax_lesson/getUserHomework',
            {
              params: {
                segment_id: sectionId,
              }
            }
          );

          const homework = res.data.data;

          const linkEl = document.querySelector<HTMLAnchorElement>('.j-homework-link')!;

          linkEl.style.display = 'none';

          if (homework) {
            document.querySelector<HTMLTextAreaElement>('.j-homework-desc')!.value = homework?.description ?? '';
            document.querySelector<HTMLInputElement>('.j-homework-section-map-id')!.value = homework?.id ?? '';

            linkEl.href = homework?.assignment ?? '';
            linkEl.style.display = 'block';

            const date = DateTime.fromISO(homework?.assignmentUploadTime, { zone: 'UTC' })
              .setZone('Asia/Taipei')
              .toFormat('yyyyMMddHHmmss');

            linkEl.setAttribute('download', title + '-' + date);

            document.getElementById('front-show-true')?.setAttribute('checked', 'true')

            if (!homework?.frontShow) {
              document.getElementById('front-show-true')?.removeAttribute('checked');
              document.getElementById('front-show-false')?.setAttribute('checked', 'true');
            }
          }

          homeworkModal.show();
        }

        if (type === 'quiz') {
          const questionList = document.querySelector<HTMLDivElement>('.c-question-list');
          const questionResultList = document.querySelector<HTMLDivElement>('.c-question-result-list');

          if (['pending', 'done', 'passed', 'failed'].includes(status)) {
            await prepareQuizResult(sectionId, quizResultModal, questionResultList, title, sectionMapId);
          } else {
            await prepareQuiz(sectionId, quizModal, questionList, title, status);
          }
        }
      });
    });

    // Quiz List
    const quizListModalEL = document.getElementById('quiz-list-modal');
    const quizListModal = new bootstrap.Modal('#quiz-list-modal', {});

    quizListModalEL?.addEventListener('show.bs.modal', async () => {
      const resultList = document.querySelector('.j-user-quiz-item');

      const sectionId = document.querySelector<HTMLButtonElement>('.j-quiz-list-btn')!.dataset.sectionId;

      resultList!.innerHTML = '';

      const res = await u.$http.get(
        '@ajax_lesson/prepareUserQuizList',
        {
          params: {
            lesson_id: lessonId,
            segment_id: sectionId
          }
        }
      );

      res.data.data.forEach((result: any) => {
        let resultItem = document.createElement('div');
        resultItem.className = 'c-quiz-result-item';
        resultItem.classList.add('d-flex');

        const date = DateTime.fromISO(result.created, { zone: 'UTC' })
          .setZone('Asia/Taipei')
          .toFormat('yyyy年MM月dd日HH:mm');

        resultItem.innerHTML = `
        <div class="c-quiz-list__time">
          ${date}
        </div>
        <div class="c-quiz-list__score text-center">
          ${result?.ans_score}/${result?.total_score}
        </div>
        <div class="c-quiz-list__score text-center">
          ${result?.correct}/${result?.total}
        </div>
        <div class="c-quiz-list__record text-center">
          <a class="text-decoration-none j-section-modal" href="javascript://"
            data-type="${result.segmentType}" data-section-id="${result.segmentId}" data-section-map-id="${result.id}">
              答題紀錄
          </a>
        </div>
      `;

        resultList?.appendChild(resultItem);
      });
    });

    quizListModalEL?.addEventListener('shown.bs.modal', async () => {
      const modalBtn = document.querySelectorAll<HTMLButtonElement>('.j-section-modal');

      modalBtn.forEach((button: HTMLButtonElement) => {
        button.addEventListener('click', async (e: Event) => {
          quizListModal.hide();

          const target = e.currentTarget as HTMLButtonElement;

          const sectionId = target.dataset!.sectionId ?? '0';
          const title = target.dataset!.sectionTitle ?? '';
          const sectionMapId = target.dataset!.sectionMapId ?? undefined;

          document.querySelector<HTMLDivElement>('.j-quiz-title')!.textContent = title;
          document.querySelector<HTMLInputElement>('.j-quiz-section-id')!.value = sectionId;

          const questionResultList = document.querySelector<HTMLDivElement>('.c-question-result-list');

          await prepareQuizResult(sectionId, quizResultModal, questionResultList, title, sectionMapId);
        });
      });
    });

    // homework submit
    const homeworkSubmitButton = document.querySelector<HTMLButtonElement>('.j-homework-submit');

    homeworkSubmitButton?.addEventListener('click', async (e) => {
      e.preventDefault();

      const target = e.currentTarget as HTMLButtonElement;

      const formElement = document.getElementById('homework-form') as HTMLFormElement;

      let formData = new FormData(formElement);

      try {
        target.setAttribute('disabled', 'true');

        await u.$http.post(
          '@ajax_lesson/updateHomework',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const v = await swal({
          title: '作業繳交成功',
        });

        if (v) {
          location.reload();
        }
      } catch (e: any) {
        console.error(e);

        await swal({
          title: e.message,
          dangerMode: true,
        });
      } finally {
        target.removeAttribute('disabled');
      }
    });

    // quiz submit
    const quizSubmitButton = document.querySelector<HTMLButtonElement>('.j-quiz-submit');
    const quizTemporaryButton = document.querySelector<HTMLButtonElement>('.j-quiz-temporary');

    quizSubmitButton?.addEventListener('click', async (e) => {
      e.preventDefault();

      const selfBtn = e.currentTarget as HTMLButtonElement;

      selfBtn?.setAttribute('disabled', 'true');

      const formElement = document.getElementById('quiz-form') as HTMLFormElement;

      let formData = new FormData(formElement);

      try {
        await u.$http.post(
          '@ajax_lesson/submitQuiz',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const v = await swal({
          title: '測驗提交成功',
        });

        if (v) {
          location.reload();
        }
      } catch (e: any) {
        console.error(e);

        await swal({
          title: '測驗提交失敗',
          text: e.message,
          dangerMode: true,
        });
      } finally {
        selfBtn?.removeAttribute('disabled');
      }
    });

    // 暫存
    quizTemporaryButton?.addEventListener('click', async (e) => {
      e.preventDefault();

      const selfBtn = e.currentTarget as HTMLButtonElement;

      selfBtn?.setAttribute('disabled', 'true');

      const formElement = document.getElementById('quiz-form') as HTMLFormElement;

      let formData = new FormData(formElement);

      formData.append('temporary', '1');

      try {
        await u.$http.post(
          '@ajax_lesson/submitQuiz',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const v = await swal({
          title: '測驗暫存成功',
        });

        if (v) {
          location.reload();
        }
      } catch (e: any) {
        console.error(e);

        await swal({
          title: '測驗暫存失敗',
          text: e.message,
          dangerMode: true,
        });
      } finally {
        selfBtn?.removeAttribute('disabled');
      }
    });

    // Re exam
    const reExam = document.querySelector('.j-re-exam') as HTMLButtonElement;

    reExam?.addEventListener('click', async (event) => {
      const target = event.currentTarget! as HTMLButtonElement;

      target.setAttribute('disabled', 'true');

      const sectionId = target.dataset.sectionId;
      const title = target.dataset.sectionTitle ?? '';

      if (sectionId === undefined) {
        return
      }

      let isPreview = false;

      await u.$http.get(
        '@ajax_lesson/checkSectionPreview',
        {
          params: {
            segment_id: sectionId,
          }
        }
      )
        .then(async (res) => {
          isPreview = res.data.data;
        })

      if (!ownedLesson) {
        if (!isPreview) {
          await swal('購買課程後即可查看內容');
          return;
        }
      }

      document.querySelector<HTMLDivElement>('.j-quiz-title')!.textContent = title;
      document.querySelector<HTMLInputElement>('.j-quiz-section-id')!.value = sectionId;

      const questionList = document.querySelector<HTMLDivElement>('.c-question-list');

      await u.$http.post(
        '@ajax_lesson/reExam',
        {
          segment_id: sectionId,
        }
      );

      const quizBtn = document.querySelector('.j-section-modal[data-section-id="' + sectionId +  '"]') as HTMLAnchorElement;

      quizBtn.dataset.status = '';

      await prepareQuiz(sectionId, quizModal, questionList, title, 'progress');

      target.removeAttribute('disabled');

      quizResultModal.hide();
    })

    // video
    const videoElement = document.querySelector<HTMLVideoElement>('#section-player');
    const player = new Plyr('#section-player');
    const rewindArea = document.querySelector<HTMLDivElement>('.rewind-area');
    const forwardArea = document.querySelector<HTMLDivElement>('.forward-area');
    const rewindIndicator = document.querySelector<HTMLDivElement>('.rewind-indicator');
    const forwardIndicator = document.querySelector<HTMLDivElement>('.forward-indicator');

    let hasSeeked = false;

    videoElement?.addEventListener('loadedmetadata', () => {
      if (hasSeeked) return;
      hasSeeked = true;

      const lastTime = parseFloat(u.data('currentTime') || '0');
      if (lastTime && !isNaN(lastTime)) {
        player.currentTime = lastTime;
      }
    });

    player?.on('ready', async () => {
      player.currentTime = u.data('currentTime');
    });

    player?.on('ended', async () => {
      await u.$http.post(
        '@ajax_lesson/videoSectionPassed',
        {
          segment_id: currentSegmentId,
          lesson_id: lessonId,
        }
      );
    });

    const seekTime = 10;

    async function showIndicator(indicator: HTMLDivElement) {
      indicator.classList.add('show');
      await reportProgress();
      setTimeout(() => {
        indicator.classList.remove('show');
      }, 600); // 顯示動畫時間
    }

    rewindArea?.addEventListener('dblclick', async () => {
      player?.rewind(seekTime);

      if (rewindIndicator !== null) {
        await showIndicator(rewindIndicator);
      }
    });

    forwardArea?.addEventListener('dblclick', async() => {
      player?.forward(seekTime);

      if (forwardIndicator !== null) {
        await showIndicator(forwardIndicator);
      }
    });

    rewindArea?.addEventListener('click', () => {
      player?.togglePlay();
    });

    forwardArea?.addEventListener('click', () => {
      player?.togglePlay();
    });

    // 鍵盤事件
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      const activeTag = (document.activeElement as HTMLElement)?.tagName;
      const isTyping = activeTag === 'INPUT' || activeTag === 'TEXTAREA';
      if (isTyping) return;

      switch (event.key) {
        case 'ArrowLeft':
          player?.rewind(seekTime);

          if (rewindIndicator !== null) {
            showIndicator(rewindIndicator);
          }

          event.preventDefault();
          break;
        case 'ArrowRight':
          player?.forward(seekTime);

          if (forwardIndicator !== null) {
            showIndicator(forwardIndicator);
          }
          event.preventDefault();
          break;
      }
    });

    let lastSentTime = 0;

    async function reportProgress() {
      if (!player) return;

      const currentTime = Math.floor(player.currentTime);
      if (currentTime !== lastSentTime) {
        lastSentTime = currentTime;

        await u.$http.post(
          '@ajax_lesson/videoSaveProgress',
          {
            lesson_id: lessonId,
            segment_id: currentSegmentId,
            current_time: currentTime,
          }
        );
      }
    }

    setInterval(reportProgress, 5000);

    // 當頁面變隱藏（切換 Tab、關閉）
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'hidden') {
        await reportProgress();
      }
    });

    // 或在離開前（備援）
    window.addEventListener('beforeunload', async () => {
      await reportProgress();
    });

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    function handleFullscreenChange() {
      const fullscreenElement = document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement;

      const rewindController = document.querySelector<HTMLDivElement>('.rewind-area');
      const forwardController = document.querySelector<HTMLDivElement>('.forward-area');

      if (fullscreenElement) {
        // 进入全屏时，将控制元素移到全屏容器内
        fullscreenElement.appendChild(rewindController);
        fullscreenElement.appendChild(forwardController);
      } else {
        // 退出全屏时，将控制元素移回原位置
        document.querySelector('#attend-video')?.appendChild(rewindController);
        document.querySelector('#attend-video')?.appendChild(forwardController);
      }
    }

    const plyrContainer = document.querySelector<HTMLDivElement>('.plyr');

    if (plyrContainer) {
      // 在捕獲階段攔截雙擊事件
      plyrContainer.addEventListener('dblclick', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const target = e.target as HTMLElement;

        // 檢查點擊位置
        if (target.closest('.rewind-area')) {
          player?.rewind(seekTime);

          if (rewindIndicator !== null) {
            await showIndicator(rewindIndicator);
          }
        } else if (target.closest('.forward-area')) {
          player?.forward(seekTime);

          if (forwardIndicator !== null) {
            await showIndicator(forwardIndicator);
          }
        }

        return false;
      }, true); // true 表示在捕獲階段處理
    }

    const target = document.querySelector<HTMLDivElement>('.plyr__menu__container')

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'hidden') {
          const isHidden = target?.hasAttribute('hidden');

          if (isHidden) {
            forwardArea!.style.zIndex = '10';
          } else {
            forwardArea!.style.zIndex = '-1';
          }
        }
      });
    });

    if (target) {
      observer.observe(target, {
        attributes: true, // 監聽屬性變化
        attributeFilter: ['hidden'] // 只監聽 hidden 屬性
      });
    }
  });

function getSectionName(array: any, value: number) {
  for (let i = 0; i < array.length; i++) {
    for (let j = 0; j < array[i].length; j++) {
      if (array[i][j].id === value) {
        return `第 ${i + 1} 章．第 ${j + 1} 節`;
      }
    }
  }

  return '';
}

// prepare quiz
async function prepareQuiz(
  sectionId: string,
  modal: Modal,
  questionList: HTMLDivElement | null,
  sectionTitle: string,
  status: string | undefined,
) {
  const res = await u.$http.get(
    '@ajax_lesson/prepareQuestionList',
    {
      params: {
        segment_id: sectionId,
      }
    }
  );

  questionList!.innerHTML = '';

  const questions: Question[] = res.data.data.questions;
  const optionsForQuestion: [Option[]] = res.data.data.optionsForQuestion;
  const userAnsGroup: [] = res.data.data.userAnsGroup;

  questions.forEach((question: Question, index: number) => {
    const userAns: UserAnswer = userAnsGroup[question.id]?.[0];
    let quizItem = document.createElement('div');
    quizItem.className = 'c-quiz-item';

    let questionTypeName = '';

    if (question.type === 'select') {
      questionTypeName = '單選題';
    } else if (question.type === 'essay') {
      questionTypeName = '申論題';
    } else if (question.type === 'boolean') {
      questionTypeName = '是非題';
    }

    let quizItemHtml = `
      <div class="card c-question-item__card">
          <div class="card-body c-question-item__card-body">
              <div class="h6 mb-1">
                  第 ${index + 1} 題（${questionTypeName}）:
                  <br>
                  ${question.title}
              </div>
              <div class="mb-3">
                  ${question.content}
              </div>
              <div>
    `;

    if (question.type === 'select') {
      optionsForQuestion[question.id]?.forEach((option: Option) => {
        quizItemHtml += `
          <div class="form-check mb-2">
              <input class="form-check-input" type="radio" ${option.id.toString() === userAns?.value[0] ? 'checked' : ''}
                  name="item[quiz][${question.id}][]" value="${option.id}">
              <label class="form-check-label">
                  ${option.title}
              </label>
          </div>
        `;
      });
    } else if (question.type === 'boolean') {
      quizItemHtml += `
        <div class="form-check mb-2">
            <input class="form-check-input" type="radio" name="item[quiz][${question.id}]" value="1" ${"1" === userAns?.value[0] ? 'checked' : ''}>
            <label class="form-check-label">
                是
            </label>
        </div>
        <div class="form-check mb-2">
            <input class="form-check-input" type="radio" name="item[quiz][${question.id}]" value="0" ${"0" === userAns?.value[0] ? 'checked' : ''}>
            <label class="form-check-label">
                否
            </label>
        </div>
      `;
    } else {
      quizItemHtml += `
        <div class="mb-2">
            <textarea class="form-control mb-2" name="item[quiz][${question.id}][value]" rows="5"
                placeholder="請填寫答案">${userAns?.value[0] !== undefined ? userAns?.value[0] : ''}</textarea>
        </div>
      `;
    }

    quizItemHtml += `</div></div></div>`;

    quizItem.innerHTML += quizItemHtml;

    questionList!.appendChild(quizItem);
  });

  document.querySelector<HTMLDivElement>('.j-quiz-title')!.textContent = sectionTitle;
  document.querySelector<HTMLInputElement>('.j-quiz-section-id')!.value = sectionId;

  modal.show();
}

async function prepareQuizResult(
  sectionId: string,
  modal: Modal,
  questionList: HTMLDivElement | null,
  sectionTitle: string | undefined,
  sectionMapId: string | undefined,
) {
  questionList!.innerHTML = '';

  const res = await u.$http.get(
    '@ajax_lesson/prepareQuestionList',
    {
      params: {
        segment_id: sectionId,
        segment_map_id: sectionMapId
      }
    }
  );

  const questions: Question[] = res.data.data.questions;
  const optionsForQuestion: [Option[]] = res.data.data.optionsForQuestion;
  const userAnsGroup: [] = res.data.data.userAnsGroup;
  let correctCount = 0;
  let total = 0;
  let score = 0;
  let totalScore = 0;

  questions.forEach((question: Question, index: number) => {
    let quizItem = document.createElement('div');
    quizItem.className = 'c-quiz-item';
    const userAns: UserAnswer = userAnsGroup[question.id]?.[0];
    let titleColor = 'text-danger';

    total += 1;
    totalScore += question.score;

    if (userAns?.isCorrect) {
      correctCount += 1;
      score += userAns.score;
      titleColor = 'text-success';
    }

    let questionTypeName = '';

    if (question.type === 'select') {
      questionTypeName = '單選題';
    } else if (question.type === 'essay') {
      questionTypeName = '申論題';

      if (!userAns.isCorrect) {
        titleColor = 'text-gray-500';
      }
    } else if (question.type === 'boolean') {
      questionTypeName = '是非題';
    }
    let quizItemHtml = `
      <div class="card c-question-item__card">
          <div class="card-body c-question-item__card-body">
              <div class="h6 mb-1">
                  <span class="${titleColor}">
                    第 ${index + 1} 題（${questionTypeName}）: ${userAns?.score} 分
                  </span>
                  <br>
                  ${question.title}
              </div>
              <div class="mb-3">
                  ${question.content}
              </div>
              <div>
    `;

    if (question.type === 'select') {
      optionsForQuestion[question.id]?.forEach((option: Option) => {
        let isCorrectText = '';

        if (option.isAnswer) {
          isCorrectText = `
          <span class="${userAns?.isCorrect ? 'text-success' : 'text-danger'}">
              正確答案 !
          </span>`;
        }

        quizItemHtml += `
          <div class="form-check mb-2">
              <input class="form-check-input" type="radio" ${option.id.toString() === userAns?.value[0] ? 'checked' : ''}
                  name="item[quiz][${question.id}][]" value="${option.id}" disabled>
              <label class="form-check-label me-3">
                  ${option.title}
              </label>
              
              ${isCorrectText}
          </div>
        `;
      });
    } else if (question.type === 'boolean') {
      quizItemHtml += `
        <div class="form-check mb-2">
            <input class="form-check-input" type="radio" ${"1" === userAns?.value[0] ? 'checked' : ''}
                name="item[quiz][${question.id}]" value="1" disabled>
            <label class="form-check-label me-3">
                是
            </label>
        `;

      if (question.answer === '1') {
        quizItemHtml += `
          <span class="${userAns?.isCorrect ? 'text-success' : 'text-danger'}">
            正確答案 !
          </span>
        `;
      }

      quizItemHtml += `
        </div>
        <div class="form-check mb-2">
            <input class="form-check-input" type="radio" ${"0" === userAns?.value[0] ? 'checked' : ''}
                name="item[quiz][${question.id}]" value="0" disabled>
            <label class="form-check-label me-3">
                否
            </label>
        `;

      if (question.answer === '0') {
        quizItemHtml += `
          <span class="${userAns?.isCorrect ? 'text-success' : 'text-danger'}">
            正確答案 !
          </span>
        `;
      }

      quizItemHtml += `
        </div>
      `;
    } else {
      quizItemHtml += `
        <div class="mb-2">
            <textarea class="form-control mb-2" name="item[quiz][${question.id}][value]" rows="5"
                placeholder="請填寫答案" disabled>${userAns?.value[0] !== undefined ? userAns?.value[0] : ''}</textarea>
        </div>
      `;

      quizItemHtml += `
        <div class="mb-2">
            <div class="c-teacher-reply">
                <div class="c-teacher-reply__title">
                    老師批閱建議
                </div>
                <div class="c-teacher-reply__content">
                    ${userAns?.teacherReply !== '' ? userAns?.teacherReply.replace(/\n/g, '<br>') : '老師批閱中！請耐心等待'}
                </div>
            </div>
        </div>
      `;
    }

    quizItemHtml += `</div></div></div>`;

    quizItem.innerHTML += quizItemHtml;

    questionList!.appendChild(quizItem);

    document.querySelector('.j-quiz-result-count')!.innerHTML = `
      答對題數 <span class="text-success">${correctCount}</span>/${total}
    `;

    document.querySelector('.j-quiz-result-score')!.innerHTML = `
      總分數 <span class="text-success">${score}</span>/${totalScore}
    `;
  });

  document.querySelector<HTMLButtonElement>('.j-re-exam')!.dataset.sectionId = sectionId;
  document.querySelector<HTMLButtonElement>('.j-re-exam')!.dataset.sectionTitle = sectionTitle;

  document.querySelector<HTMLButtonElement>('.j-quiz-list-btn')!.dataset.sectionId = sectionId;

  modal.show();
}


